import { createContext, useContext, useMemo, useState } from 'react';
import type { JSX, PropsWithChildren, Dispatch, SetStateAction } from 'react';
import type { CSSProperties } from 'styled-components';

type SelectedItemMeasurements = {
  offsetHeight: number;
  offsetLeft: number;
  offsetWidth: number;
};

type SelectedItemStyleContextValue = {
  setSelectedItemMeasurements: Dispatch<SetStateAction<SelectedItemMeasurements | null>>;
  selectedItemIndicatorStyle: Pick<CSSProperties, 'height' | 'transform' | 'width'> | undefined;
};

const SelectedItemStyleContext = createContext<SelectedItemStyleContextValue | null>(null);

type SelectedItemStyleProviderProps = PropsWithChildren;

export const SelectedItemStyleProvider = ({
  children,
}: SelectedItemStyleProviderProps): JSX.Element => {
  const [selectedItemMeasurements, setSelectedItemMeasurements] =
    useState<SelectedItemMeasurements | null>(null);

  const selectedItemIndicatorStyle = useMemo(
    () =>
      selectedItemMeasurements != null
        ? {
            height: `${selectedItemMeasurements.offsetHeight}px`,
            transform: `translateX(${selectedItemMeasurements.offsetLeft}px) translateY(-50%)`,
            width: `${selectedItemMeasurements.offsetWidth}px`,
          }
        : undefined,
    [selectedItemMeasurements],
  );

  const contextValue = useMemo(
    () => ({
      setSelectedItemMeasurements,
      selectedItemIndicatorStyle,
    }),
    [selectedItemIndicatorStyle],
  );

  return (
    <SelectedItemStyleContext.Provider value={contextValue}>
      {children}
    </SelectedItemStyleContext.Provider>
  );
};

export const useSelectedItemStyle = (): SelectedItemStyleContextValue => {
  const context = useContext(SelectedItemStyleContext);
  if (context === null) {
    throw new Error('useSelectedItemStyle must be used within a SelectedItemStyleProvider');
  }
  return context;
};
